import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { trackStorefrontClick } from '../utils/trackClick';
import { 
  ArrowLeft, DollarSign, TrendingUp, Users, Sparkles, 
  CheckCircle, ArrowRight, Search, Store, Percent, 
  Zap, Shield, CreditCard, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import Logo from '@/components/Logo';
import CreatorAvatar from '@/components/CreatorAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AffiliateProgram() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [creators, setCreators] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [user, setUser] = useState(null);
  
  // Get creator from checkout (if redirected from there)
  const fromCheckout = location.state?.fromCheckout;
  const purchasedFromCreator = location.state?.creatorId;
  const purchasedCreatorName = location.state?.creatorName;

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    loadCreatorsWithAffiliates();
  }, []);

  const loadCreatorsWithAffiliates = async () => {
    try {
      // Load creators who have ≥1 product with affiliate program enabled
      const res = await axios.get(`${API_URL}/api/creators/with-affiliates`);
      setCreators(res.data.creators || []);
    } catch (error) {
      // No fallback to /api/creators — that would leak test profiles &
      // sellers with zero affiliate products onto the join page (which
      // is exactly the bug we're fixing). Empty state is correct here.
      console.error('Error loading creators with affiliates:', error);
      setCreators([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredCreators = creators.filter(creator => 
    creator.store_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    creator.bio?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const benefits = [
    {
      icon: DollarSign,
      title: "Earn Commissions",
      description: "Get paid for every sale you refer. Commission rates vary by product.",
      color: "#00FF94"
    },
    {
      icon: Zap,
      title: "Instant Payouts",
      description: "Receive your earnings instantly via Stripe Connect.",
      color: "#00F0FF"
    },
    {
      icon: Shield,
      title: "Trusted Platform",
      description: "Join thousands of affiliates earning on theailedger.ai",
      color: "#00F0FF"
    },
    {
      icon: TrendingUp,
      title: "Track Performance",
      description: "Real-time analytics to monitor clicks, conversions, and earnings.",
      color: "#00F0FF"
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-neutral-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <Logo />
          <div className="w-20" />
        </div>
      </header>

      <div className="container mx-auto px-6 py-12 max-w-5xl">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00FF94]/20 to-[#00F0FF]/20 border border-[#00FF94]/30 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4 text-[#00FF94]" />
            <span className="text-sm font-semibold text-[#00FF94]">Affiliate Program</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-black mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Become an Affiliate & <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FF94] to-[#00F0FF]">Earn 💰</span>
          </h1>
          
          <p className="text-xl text-neutral-400 max-w-2xl mx-auto mb-8">
            Share AI products you love and earn commissions on every sale. 
            Join our affiliate program and start earning today!
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {benefits.map((benefit, idx) => (
            <div 
              key={idx}
              className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-all"
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: `${benefit.color}20` }}
              >
                <benefit.icon className="w-6 h-6" style={{ color: benefit.color }} />
              </div>
              <h3 className="font-bold mb-2">{benefit.title}</h3>
              <p className="text-sm text-neutral-400">{benefit.description}</p>
            </div>
          ))}
        </div>

        {/* How It Works */}
        <div className="bg-gradient-to-br from-white/10 to-[#00F0FF]/10 border border-white/10 rounded-3xl p-8 mb-16">
          <h2 className="text-2xl font-bold mb-8 text-center">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-[#00F0FF] text-black flex items-center justify-center text-xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="font-bold mb-2">Choose a Creator</h3>
              <p className="text-sm text-neutral-400">
                Browse creators with affiliate programs and find products you believe in.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-white text-black flex items-center justify-center text-xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="font-bold mb-2">Get Your Link</h3>
              <p className="text-sm text-neutral-400">
                Generate your unique affiliate link and share it with your audience.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-[#00FF94] text-black flex items-center justify-center text-xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="font-bold mb-2">Earn Commissions</h3>
              <p className="text-sm text-neutral-400">
                Get paid for every sale made through your link. Track everything in real-time.
              </p>
            </div>
          </div>
        </div>

        {/* If came from checkout, show the creator they purchased from */}
        {fromCheckout && purchasedFromCreator && (
          <div className="bg-gradient-to-r from-[#00FF94]/20 to-[#00F0FF]/20 border border-[#00FF94]/30 rounded-2xl p-6 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00FF94] to-[#00F0FF] flex items-center justify-center">
                <Store className="w-7 h-7 text-black" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-[#00FF94] mb-1">You just purchased from</p>
                <h3 className="text-xl font-bold">{purchasedCreatorName || 'This Creator'}</h3>
                <p className="text-sm text-neutral-400">Become an affiliate and earn on future referrals!</p>
              </div>
              <Button
                onClick={() => navigate(`/creator/${purchasedFromCreator}`)}
                className="bg-[#00FF94] hover:bg-[#00DD7F] text-black font-bold rounded-full"
              >
                Join Their Program
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Search Creators */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
            <Users className="w-6 h-6 text-[#00F0FF]" />
            Find Creators with Affiliate Programs
          </h2>
          
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search creators by name or niche..."
              className="pl-12 bg-neutral-900 border-white/10 rounded-full py-6"
            />
          </div>
        </div>

        {/* Creators List */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 animate-pulse">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-neutral-800" />
                  <div className="flex-1">
                    <div className="h-5 bg-neutral-800 rounded w-32 mb-2" />
                    <div className="h-4 bg-neutral-800 rounded w-20" />
                  </div>
                </div>
                <div className="h-16 bg-neutral-800 rounded" />
              </div>
            ))}
          </div>
        ) : filteredCreators.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCreators.map((creator) => (
              <div 
                key={creator.creator_id}
                className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 hover:border-[#00F0FF]/50 transition-all cursor-pointer group"
                onClick={() => { trackStorefrontClick(creator.store_slug, 'affiliate_program_grid'); navigate(`/store/${creator.store_slug}`); }}
              >
                <div className="flex items-center gap-4 mb-4">
                  <CreatorAvatar
                    storeName={creator.store_name}
                    avatarUrl={creator.avatar}
                    size={56}
                    className="rounded-xl"
                  />
                  <div className="flex-1">
                    <h3 className="font-bold group-hover:text-[#00F0FF] transition-colors">{creator.store_name}</h3>
                    <p className="text-sm text-neutral-400">{creator.total_products || 0} products</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-neutral-600 group-hover:text-[#00F0FF] transition-colors" />
                </div>
                <p className="text-sm text-neutral-400 line-clamp-2 mb-4">
                  {creator.bio || 'AI product creator on theailedger.ai'}
                </p>
                <div className="flex items-center gap-2 text-sm">
                  <Percent className="w-4 h-4 text-[#00FF94]" />
                  <span className="text-[#00FF94]">Affiliate program available</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Store className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">No creators found</h3>
            <p className="text-neutral-400 mb-6">Try a different search term</p>
            <Button
              onClick={() => setSearchQuery('')}
              variant="outline"
              className="border-white/20"
            >
              Clear Search
            </Button>
          </div>
        )}

        {/* Not logged in prompt */}
        {!user && (
          <div className="mt-12 bg-neutral-900 border border-white/10 rounded-2xl p-8 text-center">
            <h3 className="text-xl font-bold mb-3">Ready to Start Earning?</h3>
            <p className="text-neutral-400 mb-6">
              Create a free account to join affiliate programs and start earning commissions.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-[#00FF94] to-[#00F0FF] hover:opacity-90 text-black font-bold rounded-full px-8"
            >
              Sign Up Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AffiliateProgram;
